/* =============================================================================
   Cart migration — keeping a basket when the customer changes store.

   Spec: C:\Shift8POS\S8MC Specs\Cart Location Change\
         Build\WebOrdering_CartLocationChange_20261008.md

   PURE. No fetch, no React, no state. Menus and cart lines in, decisions and
   payloads out — so this can be reasoned about on its own, and later tested
   without standing a server up.

   ⚠ TWO MENUS, NOT CART PRICES. Repricing is decided by comparing the OLD
   store's menu against the NEW store's menu, both keyed on PLU — never by
   comparing a cart line's price against a menu. A combo line's priceInc
   already carries its substitution uplift (see the clamp in
   TemporaryUserCartRepository), so comparing it to a menu's base combo price
   reports a price change that is not real. Menu-to-menu is exact for both
   regular products and combos.

   ⚠ The browser still never prices the basket. These numbers are for the
   warning the customer reads. The authoritative total comes from re-reading
   the cart after the rebuild — see BR-03 in the spec.

   ⚠ PLU IS THE IDENTITY. Confirmed by Jono, 8 Oct: a PLU is always the same
   product across stores within a brand. Nothing here matches on name or on
   productID, both of which are free to differ.
   ============================================================================= */

/* ---------------------------------------------------------------- menu index

   The menu arrives as productDetails[] -> products[], each product carrying
   productPLUNumberInt, priceInc, isSoldOut and modCategories[] -> mods[].

   Flattened to two lookups:
     products : PLU -> { priceInc, isSoldOut, name }
     modPLUs  : Set of every modifier PLU offered anywhere in the menu

   ⚠ modPLUs is deliberately menu-wide rather than per product. A modifier
   category is shared between products, so "is this modifier sold here at all"
   is the question that matters, and scoping it per product would reject lines
   for a difference that does not exist. */
export function buildMenuIndex(menuCategories) {
    const products = new Map();
    const modPLUs = new Set();
    /* Modifier PLU -> price at THIS store. Needed because a modifier can be
       priced differently between stores (Caramel Sauce: $0.20 at Carindale,
       $0.70 at Sam Testing). Comparing only product base prices told the
       customer "$6.00 -> $4.20" when the line really went $6.20 -> $4.90 —
       found in the 8 Oct test. */
    const modPrices = new Map();

    (menuCategories || []).forEach((cat) => {
        (cat.products || []).forEach((p) => {
            const plu = pluOf(p.productPLUNumberInt, p.productPLUNumber);
            if (plu === null) { return; }

            // First definition wins. A product listed in two categories is the
            // same product at the same price; overwriting would be harmless but
            // this makes the intent explicit.
            if (!products.has(plu)) {
                products.set(plu, {
                    priceInc: Number(p.priceInc),
                    isSoldOut: p.isSoldOut === true,
                    name: p.productName || p.productDisplayName || ''
                });
            }

            (p.modCategories || []).forEach((mc) => {
                (mc.mods || []).forEach((m) => {
                    const modPlu = pluOf(m.modPLUNumberInt, m.modPLUNumber);
                    if (modPlu !== null) {
                        modPLUs.add(modPlu);
                        const price = Number(m.priceInc);
                        if (!modPrices.has(modPlu) && Number.isFinite(price)) {
                            modPrices.set(modPlu, price);
                        }
                    }
                });
            });
        });
    });

    return { products, modPLUs, modPrices };
}

/* PLUs arrive as a number on one field and a string on another depending on the
   endpoint. Normalise to Number, and reject anything that is not a real number
   rather than letting NaN into a Map key — NaN !== NaN, so a NaN key can never
   be looked up again and the line would silently classify as removed. */
function pluOf(intValue, stringValue) {
    const n = Number(intValue !== undefined && intValue !== null ? intValue : stringValue);
    return Number.isFinite(n) ? n : null;
}

/* ---------------------------------------------------------------- classify */

/** Why a line cannot come across. */
export const REMOVED_NOT_SOLD = 'notSold';
export const REMOVED_SOLD_OUT = 'soldOut';
export const REMOVED_PART_MISSING = 'partMissing';   // a combo's inner product
export const REMOVED_MOD_MISSING = 'modMissing';     // a chosen modifier

/**
 * Decide what happens to every line in the basket.
 *
 * @param {Array}  cartProducts   cartDetails.products from the cart read
 * @param {Object} oldIndex       buildMenuIndex(current store's menu)
 * @param {Object} newIndex       buildMenuIndex(new store's menu)
 * @returns {{carried: Array, repriced: Array, removed: Array}}
 *
 * repriced entries carry { line, oldPrice, newPrice }.
 * removed entries carry  { line, reason, detail }.
 *
 * ⚠ A line appears in exactly one bucket. Repriced lines are KEPT — they are
 * reported, not dropped.
 */
export function classify(cartProducts, oldIndex, newIndex) {
    const carried = [];
    const repriced = [];
    const removed = [];

    (cartProducts || []).forEach((line) => {
        // System lines the server injects — surcharge and service fee. They are
        // not the customer's choices and are re-derived at the new store, so
        // they are neither carried nor reported.
        if (isSystemLine(line)) { return; }

        const verdict = classifyLine(line, oldIndex, newIndex);
        if (verdict.bucket === 'removed') { removed.push(verdict); }
        else if (verdict.bucket === 'repriced') { repriced.push(verdict); }
        else { carried.push(verdict); }
    });

    return { carried, repriced, removed };
}

/* The two PLUs the server adds itself. CartProduct.js keys off the same two
   numbers (999999000 surcharge, 999999010 service fee). */
const SURCHARGE_PLU = 999999000;
const SERVICE_FEE_PLU = 999999010;

function isSystemLine(line) {
    const plu = Number(line.productPLU);
    return plu === SURCHARGE_PLU || plu === SERVICE_FEE_PLU;
}

function classifyLine(line, oldIndex, newIndex) {
    const plu = Number(line.productPLU);
    const target = newIndex.products.get(plu);

    if (!target) {
        return { bucket: 'removed', line, reason: REMOVED_NOT_SOLD, detail: null };
    }
    // Q4, answered: sold out counts as unavailable. Kept as its own reason so
    // the modal can say "not available right now" rather than "not sold here".
    if (target.isSoldOut) {
        return { bucket: 'removed', line, reason: REMOVED_SOLD_OUT, detail: null };
    }

    // A product combo only survives if every product inside it does.
    const missingPart = firstMissingPart(line, newIndex);
    if (missingPart) {
        return { bucket: 'removed', line, reason: REMOVED_PART_MISSING, detail: missingPart };
    }

    // BR-05, Q3 answered: a line whose chosen modifier is not sold at the new
    // store is REMOVED, not quietly simplified. Handing someone a coffee
    // without the syrup they picked — at a price that assumed it — is worse
    // than telling them it is unavailable.
    const missingMod = firstMissingMod(line, newIndex);
    if (missingMod) {
        return { bucket: 'removed', line, reason: REMOVED_MOD_MISSING, detail: missingMod };
    }

    // Menu-to-menu, per the header. A PLU absent from the old index means we
    // cannot say anything truthful about a change, so treat it as unchanged
    // and let the server's re-read be the answer.
    const source = oldIndex.products.get(plu);
    if (source && Number.isFinite(source.priceInc) && Number.isFinite(target.priceInc)) {
        /* The UNIT price the customer pays for this line = the product's price
           plus every modifier they chose, each at that store's own price.

           ⚠ Comparing the product price alone understated the change in the
           8 Oct test: "$6.00 -> $4.20" was shown while the line really went
           $6.20 -> $4.90, because Caramel Sauce costs $0.20 at one store and
           $0.70 at the other.

           ⚠ The one thing NOT replicated here is a combo's substitution uplift
           — that is the server's clamp logic (TemporaryUserCartRepository),
           and duplicating pricing rules in the browser is what BR-03 forbids.
           So for a combo with a substitution, these figures are the base combo
           plus its modifiers; the total after the switch is still the server's. */
        const oldUnit = source.priceInc + modTotal(line, oldIndex);
        const newUnit = target.priceInc + modTotal(line, newIndex);
        if (Number.isFinite(oldUnit) && Number.isFinite(newUnit) && !samePrice(oldUnit, newUnit)) {
            return {
                bucket: 'repriced', line,
                oldPrice: oldUnit,
                newPrice: newUnit
            };
        }
    }

    return { bucket: 'carried', line };
}

/* Sum of every chosen modifier on the line, at one store's prices — on the line
   itself, on each product inside a combo, and nested inside modifier combos.
   A modifier the index has no price for contributes 0 rather than NaN; its
   absence has already been caught by firstMissingMod for the NEW store, and
   for the OLD store a missing price means "unknown", not "free". */
function modTotal(line, index) {
    let sum = 0;
    const add = (mods) => {
        (mods || []).forEach((mod) => {
            if (Number(mod.itemUsageTypeID) !== 2) {
                const p = index.modPrices.get(Number(mod.modPLU));
                if (Number.isFinite(p)) {
                    sum += p * (Number(mod.quantity) > 0 ? Number(mod.quantity) : 1);
                }
            }
            add(mod.modsInCombo);
        });
    };
    add(line.mods);
    (line.productsInCombo || []).forEach((part) => add(part.mods));
    return sum;
}

/* Money compared in whole cents. 2.80 - 2.80 is not reliably 0 in binary
   floating point, and a phantom one-cent reprice on every line would destroy
   trust in the warning. */
function samePrice(a, b) {
    return Math.round(a * 100) === Math.round(b * 100);
}

function firstMissingPart(line, newIndex) {
    const parts = line.productsInCombo || [];
    for (let i = 0; i < parts.length; i++) {
        const partPlu = Number(parts[i].productPLU);
        const found = newIndex.products.get(partPlu);
        if (!found || found.isSoldOut) {
            return parts[i].productName || String(partPlu);
        }
    }
    return null;
}

/* Walks all four levels a modifier can live at: on the line, on a product
   inside a combo, and — for a modifier combo — the mods nested inside either. */
function firstMissingMod(line, newIndex) {
    const direct = missingModIn(line.mods, newIndex);
    if (direct) { return direct; }

    const parts = line.productsInCombo || [];
    for (let i = 0; i < parts.length; i++) {
        const inPart = missingModIn(parts[i].mods, newIndex);
        if (inPart) { return inPart; }
    }
    return null;
}

function missingModIn(mods, newIndex) {
    const list = mods || [];
    for (let i = 0; i < list.length; i++) {
        const mod = list[i];
        const modPlu = Number(mod.modPLU);
        // A modifier-combo parent (itemUsageTypeID 2) is a container; what has
        // to be available is the mods inside it.
        if (Number(mod.itemUsageTypeID) !== 2) {
            if (Number.isFinite(modPlu) && !newIndex.modPLUs.has(modPlu)) {
                return mod.modName || String(modPlu);
            }
        }
        const nested = missingModIn(mod.modsInCombo, newIndex);
        if (nested) { return nested; }
    }
    return null;
}

/* ---------------------------------------------------------------- rebuild

   ⚠ THE READ SHAPE AND THE WRITE SHAPE ARE THE SAME TYPE. Verified 8 Oct:
   UserCartV2Controller.Post takes [FromBody] CartUserTempViewModel — exactly
   what the GET returns as `cartDetails`. The insert loop reads
   ProductCategoryId, ProductID, ProductPLU, Quantity, SpecialInstructions,
   ItemUsageTypeID, ProductComboGroupID, CalculatePriceDifference,
   OriginalProductID, OriginalProductPLUNumber and BulkPricingProductsID, then
   recurses through ProductsInCombo -> Mods -> ModsInCombo. Every one of those
   fields is present on the line the GET handed us.

   So this is a field-for-field remap, not a reconstruction. The spec's first
   draft called this "the hard part" on the strength of the UI's add-to-cart
   builder being branched by item type; that builder is branched because it is
   translating FORM state, which is a different problem. Rebuilding from a cart
   line is not.

   ⚠ Case is converted deliberately. The wire format out is PascalCase, to
   match what the app's own add-to-cart posts. Web API's binder happens to be
   case-insensitive, so camelCase would also bind — relying on that would make
   this code look wrong to the next reader and break the day someone configures
   a strict resolver. */

/** One cart line -> the POST body that recreates it at `envelope.LocationNumber`. */
export function toAddPayload(line, envelope) {
    return {
        DeviceUID: envelope.DeviceUID,
        MemberId: envelope.MemberId,
        CardNumber: envelope.CardNumber,
        BrandCode: envelope.BrandCode,
        LocationNumber: parseInt(envelope.LocationNumber, 10),
        DeviceType: envelope.DeviceType || 'iOS',
        ProductPriceTierID: envelope.ProductPriceTierID,
        ModPriceTierID: envelope.ModPriceTierID,
        Products: [productOut(line)]
    };
}

function productOut(line) {
    return {
        ProductCategoryId: intOr0(line.productCategoryId),
        ProductID: intOr0(line.productID),
        ProductPLU: intOr0(line.productPLU),
        Quantity: intOr1(line.quantity),
        SpecialInstructions: line.specialInstructions || '',
        ItemUsageTypeID: intOr1(line.itemUsageTypeID),
        // ⚠ Carried through verbatim. These three are how a substitution inside
        // a combo is recorded, and the server reads them to work out the price
        // difference. Dropping them turns a substituted combo back into the
        // default one at the default price.
        CalculatePriceDifference: line.calculatePriceDifference === true,
        OriginalProductID: line.originalProductID === undefined ? null : line.originalProductID,
        OriginalProductPLUNumber: line.originalProductPLUNumber === undefined ? null : line.originalProductPLUNumber,
        ProductComboGroupID: intOr0(line.productComboGroupID),
        BulkPricingProductsID: intOr0(line.bulkPricingProductsID),
        ProductsInCombo: (line.productsInCombo || []).map(productOut),
        Mods: (line.mods || []).map(modOut)
    };
}

function modOut(mod) {
    return {
        ModCategoryId: intOr0(mod.modCategoryId),
        ModID: intOr0(mod.modID),
        ModPLU: intOr0(mod.modPLU),
        Quantity: intOr1(mod.quantity),
        ItemUsageTypeID: intOr1(mod.itemUsageTypeID),
        // null, not [], when there is nothing nested — the app's own builder
        // sends null for a plain modifier and an array only for a mod combo.
        ModsInCombo: (mod.modsInCombo && mod.modsInCombo.length)
            ? mod.modsInCombo.map(modOut)
            : null
    };
}

function intOr0(v) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : 0;
}

/* Quantity defaults to 1, never 0 — a 0 would post a line that cannot exist
   and the server would either reject it or create a free item. */
function intOr1(v) {
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : 1;
}

const CartMigration = {
    buildMenuIndex, classify, toAddPayload,
    REMOVED_NOT_SOLD, REMOVED_SOLD_OUT, REMOVED_PART_MISSING, REMOVED_MOD_MISSING
};
export default CartMigration;
